import { useEffect, useState, useCallback } from "react";

//Mui icons
import {
  Paper,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';

//Common styles
import {
  ButtonContainer,
  CustomTableRowTR,
  FilledButton,
  OutlinedButton,
  OutlinedDelButton,
  StyledTable,
  TableData,
  TableHeader,
  TableTopHeader,
  TableWrapper,
  Title,
} from "../../styles/commonStyles";

//Services
import { CuisineDishService } from "../../services/CuisineDishService";

//Events
import EventBus from "../../EventBus";

//Enum
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";

//Common COmponents
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import UpdateIngredientDialog from "./UpdateIngredientDialog";
import AddIngredientDialog from "./AddIngredientDialog";

interface CuisineIngredient {
  id: number;
  dishId: number;
  name: string;
  quantity: number;
  unitOfMeasurement: string;
}

interface Props {
  dishId: number;
}

const cuisineDishService = new CuisineDishService();

const CuisineIngredients = ({ dishId }: Props) => {
  const [ingredients, setIngredients] = useState<CuisineIngredient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const [selectedIngredient, setSelectedIngredient] =
    useState<CuisineIngredient | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const fetchDishIngredients = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await cuisineDishService.getDishIngredients(dishId);
      setIngredients(response?.data ?? []);
    } catch {
      setError("Failed to load ingredients");
    } finally {
      setLoading(false);
    }
  }, [dishId]);

  useEffect(() => {
    fetchDishIngredients();
  }, [fetchDishIngredients]);

  const handleAddIngredient = async (
  ingredientId: number,
  quantity: string,
  onClose: () => void
  ) => {
    if (!ingredientId || !quantity) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Ingredient and quantity are required",
        type: CustomSnackbarTypes.ERROR,
      });
      return;
    }

    try {
      await cuisineDishService.addDishIngredientById(dishId, {
        ingredientId,
        quantity,
      });

      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Ingredient added successfully",
        type: CustomSnackbarTypes.SUCCESS,
      });

      onClose();
      fetchDishIngredients();
    } catch (error) {
      console.error("Error adding ingredient:", error);

      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Failed to add ingredient",
        type: CustomSnackbarTypes.ERROR,
      });
    }
  };


  const handleUpdateIngredient = async (quantity: string) => {
  if (!selectedIngredient) return;

  try {
    await cuisineDishService.updateDishIngredient(
      selectedIngredient.id,
      { quantity }
    );

    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
      message: "Ingredient updated successfully",
      type: CustomSnackbarTypes.SUCCESS,
    });

    setEditOpen(false);
    setSelectedIngredient(null);
    fetchDishIngredients();
  } catch {
    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
      message: "Failed to update ingredient",
      type: CustomSnackbarTypes.ERROR,
    });
  }
};

  const handleDeleteIngredient = (ingredientId: number) => {
    EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
      title: "Delete Ingredient",
      message: "Are you sure you want to delete this ingredient?",
      onClose: async (confirmed: boolean) => {
        if (!confirmed) return;

        try {
          await cuisineDishService.deleteDishIngredientById(ingredientId);

          EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
            message: "Ingredient deleted successfully",
            type: CustomSnackbarTypes.SUCCESS,
          });

          fetchDishIngredients();
        } catch {
          EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
            message: "Failed to delete ingredient",
            type: CustomSnackbarTypes.ERROR,
          });
        }
      },
    });
  };

  return (
    <TableWrapper>
      <TableTopHeader>
        <Title>Ingredients List</Title>
          <FilledButton onClick={() => setAddOpen(true)}>
            <AddIcon sx={{ color: "#fff !important", fontSize: 18 }} />
            Add Ingredient
          </FilledButton>

      </TableTopHeader>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Box py={4} textAlign="center">
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {!loading && !error && ingredients.length === 0 && (
        <Box py={4} textAlign="center">
          <Typography color="text.secondary">
            No ingredients found
          </Typography>
        </Box>
      )}

      {!loading && !error && ingredients.length > 0 && (
        <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
          <StyledTable>
            <TableHead>
              <TableRow sx={{ backgroundColor: "rgba(232,235,237,0.3) !important" }}>
                <TableHeader>ID</TableHeader>
                <TableHeader>Name</TableHeader>
                <TableHeader>Quantity</TableHeader>
                <TableHeader>Unit</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>

            <TableBody>
              {ingredients.map((row) => (
                <CustomTableRowTR key={row.id}>
                  <TableData>{row.id || "-"}</TableData>
                  <TableData>{row.name || "-"}</TableData>
                  <TableData>{row.quantity || "-"}</TableData>
                  <TableData>{row.unitOfMeasurement || "-"}</TableData>

                  <TableData  style={{maxWidth: 70}}>
                    <ButtonContainer style={{ maxWidth: 120 }}>
                      <OutlinedButton style={{padding: "0px 12px", fontWeight: "normal"}} variant={"contained"} size="small" onClick={() => {
                          setSelectedIngredient(row);
                          setEditOpen(true);
                        }}>
                        Edit
                      </OutlinedButton>

                      <OutlinedDelButton style={{padding: "0px 12px", fontWeight: "normal"}} variant={"contained"} size="small" onClick={() => handleDeleteIngredient(row.id)}>
                        Delete
                      </OutlinedDelButton>

                    </ButtonContainer>
                  </TableData>
                </CustomTableRowTR>
              ))}
            </TableBody>
          </StyledTable>
        </TableContainer>
      )}

      {selectedIngredient && (
        <UpdateIngredientDialog
          open={editOpen}
          ingredient={selectedIngredient}
          onClose={() => setEditOpen(false)}
          onSubmit={handleUpdateIngredient}
        />
      )}

      <AddIngredientDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={(ingredientId, quantity) =>
          handleAddIngredient(ingredientId, quantity, () => setAddOpen(false))
        }
      />

    </TableWrapper>
  );
};

export default CuisineIngredients;
